import { Operation, OpKind, Point, Swing } from './types';
import { Monkey } from './monkey';
import { Coin } from './coin';
import { Scene } from 'phaser';
import { EventEmitter } from 'events';
import Graphics = Phaser.GameObjects.Graphics;
import Vector2 = Phaser.Math.Vector2;
import Curve = Phaser.Curves.Curve;

export class Game {
    public isPlaying: boolean;
    public coins: Coin[] = [];
    public monkey: Monkey;
    public path: { t: number; vec: Phaser.Math.Vector2 };
    public swingData: Swing[][];
    public eventEmitter = new EventEmitter();
    public coinsContainer: Phaser.GameObjects.Container | null;
    public newCurvePoints: Vector2[] = [];
    public vinePositions: Vector2[] = [];
    public mainCoins: Coin[] = [];
    public fallPoints: Phaser.Math.Vector2[];
    private points: number[];
    private operations: Operation[];
    private curve: Curve;
    private collectedCoins: Coin[] = [];
    private curvesPoints: Point[][] = [];
    private readonly minY = 1080 * 0.1;
    private readonly maxY = 1080 * 0.8;
    private swingsCount = 2;
    private readonly pathSpeedPerPoint = 1;
    private graphics: Graphics;
    private allCoinsCollected: boolean;
    private swingMaxY: number;

    constructor(
        private startPosition: Point,
        private gameWidth: number,
        private gameHeight: number,
        private dummyCoinsCount: number,
    ) {
        this.minY = 0;
        this.swingMaxY = gameHeight * 0.6;
        this.maxY = gameHeight * 0.8;
        this.initialize();
    }

    public start(operations: Operation[], scene: Scene, parent?): void {
        this.operations = operations;
        this.isPlaying = true;
        this.reset();
        if (this.coinsContainer) {
            this.coinsContainer.destroy(true);
            this.coinsContainer = null;
        }
        this.coinsContainer = scene.add.container();
        this.coinsContainer.x = this.startPosition.x;
        this.coinsContainer.y = this.startPosition.y;
        this.coinsContainer.setSize(this.newCurvePoints[this.newCurvePoints.length - 1].x, this.gameHeight);
        parent.add(this.coinsContainer);
        scene.tweens.addCounter({
            from: 0,
            to: this.newCurvePoints.length - 1,
            duration: this.newCurvePoints.length * this.pathSpeedPerPoint,
            onUpdate: (tween, targets, key, current, previous, param) => {
                const index = Math.ceil(current);
                this.monkey.position.y = this.newCurvePoints[index].y;
                if (this.coinsContainer) {
                    this.coinsContainer.x = -this.newCurvePoints[index].x;
                }
                this.checkToSwing();
                this.collisionCheck();
                this.isLastSwing();
            },
            onComplete: () => {
                this.isPlaying = false;
                const index = this.newCurvePoints.length - 1;
                this.monkey.position.y = this.newCurvePoints[index].y;
                if (this.coinsContainer) {
                    this.coinsContainer.x = -this.newCurvePoints[index].x;
                }
                this.eventEmitter.emit('complete');
            },
        });
    }

    private reset(): void {
        this.monkey.position.x = this.startPosition.x;
        this.monkey.position.y = this.startPosition.y;
        this.points = [];
        this.coins = [];
        this.mainCoins = [];
        this.collectedCoins = [];
        this.curvesPoints = [];
        this.vinePositions = [];
        this.newCurvePoints = [];
        this.allCoinsCollected = false;
        this.fallPoints = [];
        this.swingData = [];
        this.swingsCount = this.operations.length > 5 ? Phaser.Math.Between(4, 10) : Phaser.Math.Between(2, 4);
        this.initPath();
    }

    private initialize(): void {
        this.initMonkey();
    }

    private initMonkey(): void {
        this.monkey = new Monkey();
        this.monkey.position.x = this.startPosition.x;
        this.monkey.position.y = this.startPosition.y;
    }

    private initPath(): void {
        this.path = { t: 0, vec: new Phaser.Math.Vector2() };
        this.points = this.getPoints();
        this.curve = new Phaser.Curves.Spline(this.points);
        this.newCurvePoints = this.curve.getPoints(this.curve.getBounds().width);
        const lowestPoint = this.newCurvePoints.find((p) => p.y >= this.maxY);
        this.newCurvePoints = this.newCurvePoints.filter(
            (p) => p.y <= this.maxY && p.x < (lowestPoint?.x ?? this.newCurvePoints[this.newCurvePoints.length - 1].x),
        );
        this.setVinesPosition();
        this.setCoinsPosition();
    }

    private debug(scene, parent?): void {
        if (this.graphics) return;
        this.graphics = scene.add.graphics();
        this.graphics.y = -500;
        this.graphics.fillStyle(0xffaaaa);
        this.newCurvePoints.forEach((p, i) => {
            if (i % 100 != 0) return;
            this.graphics.fillCircle(p.x, p.y, 2);
        });
        this.vinePositions.forEach((v) => {
            this.graphics.fillStyle(0xffffaa);
            this.graphics.fillCircle(v.x, v.y, 10);
        });
        this.coinsContainer && this.coinsContainer.add(this.graphics);
    }

    private getPoints(): number[] {
        const gapX = this.gameWidth / 3.5,
            gapY = this.gameHeight * 0.4;
        const defaultCurvePoints = [
            [
                { x: this.startPosition.x, y: this.startPosition.y },
                { x: this.startPosition.x + gapX, y: this.startPosition.y + gapY },
                { x: this.startPosition.x + gapX * 2, y: this.startPosition.y },
            ],
        ];

        this.swingData = this.distributeStable(this.operations, this.swingsCount, 6);
        console.error('this.swingData', this.swingData);
        let stepY = 0;
        const deltaSwingUp = 210;
        const deltaSwingDown = 100;

        this.swingData.forEach((currentOp, index) => {
            const currentPos = defaultCurvePoints[index];
            const lastPos = defaultCurvePoints[Math.max(0, index - 1)];
            const willIncrease = this.willIncrease(1, currentOp);
            const lastY = lastPos[currentPos.length - 1].y;
            const lastX = currentPos[currentPos.length - 1].x;
            if (willIncrease) {
                stepY = stepY - deltaSwingUp;
                if (!stepY) stepY -= deltaSwingUp;
            } else {
                stepY = !currentOp.length ? stepY + 20 : stepY + deltaSwingDown;
                if (!stepY) {
                    stepY = !currentOp.length ? stepY + 20 : stepY + deltaSwingDown;
                }
            }
            const x1 = lastX + gapX * 1.2;
            const x2 = x1 + gapX;
            const x3 = x2 + gapX;
            const y1 = Math.min(Math.max(this.minY, lastY + stepY), this.swingMaxY - 150);
            const y2 = Math.min(Math.max(this.minY, y1 + gapY), this.swingMaxY);
            const y3 = y1;
            const newPos = [
                { x: x1, y: y1 },
                { x: x2, y: y2 },
                { x: x3, y: y3 },
            ];
            defaultCurvePoints.push(newPos);
        });

        const endCurve = defaultCurvePoints[defaultCurvePoints.length - 1];
        const lastY = endCurve[2].y;
        const lastX = endCurve[2].x;
        const x1 = lastX + gapX * 1.2;
        const x2 = x1 + gapX;
        const x3 = x2 + gapX;
        const y1 = Math.min(Math.max(this.minY, lastY), this.maxY);
        const y2 = Math.min(Math.max(this.minY, y1 + gapY, this.maxY));
        const y3 = this.maxY;
        const newPos = [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
            { x: x3, y: y3 },
        ];
        defaultCurvePoints.push(newPos);
        this.curvesPoints = defaultCurvePoints;
        return this.toFlatXY(defaultCurvePoints);
    }

    private distributeStable(ops: Operation[], bucketCount = 6, maxPerBucket = 2, rng = Math.random): Swing[][] {
        const capacity = bucketCount * maxPerBucket;
        const availableSlots = capacity - maxPerBucket;
        const buckets: Swing[][] = Array.from({ length: bucketCount }, () => []);
        const slots = Array.from({ length: availableSlots }, (_, i) => i + maxPerBucket);
        for (let i = slots.length - 1; i > 0; i--) {
            const j = Math.floor(rng() * (i + 1));
            [slots[i], slots[j]] = [slots[j], slots[i]];
        }
        const chosen = slots.slice(0, Math.min(ops.length, availableSlots)).sort((a, b) => a - b);
        chosen.forEach((slotIndex, k) => {
            const bucketIndex = Math.floor(slotIndex / maxPerBucket);
            const bucket = buckets[bucketIndex];
            bucket.push({ op: ops[k] });
        });

        return buckets;
    }

    private toFlatXY(data: Point[] | Point[][]): number[] {
        const paths: Point[][] = Array.isArray((data as any)[0]) ? (data as Point[][]) : [data as Point[]];
        const out: number[] = [];
        for (const path of paths) {
            for (const p of path) {
                out.push(Number(p.x), Number(p.y));
            }
        }
        return out;
    }

    private willIncrease(current: number, swings: Swing[]): boolean {
        const old = current;
        swings.forEach((swing) => {
            const { op } = swing;
            switch (op.function) {
                case 'add':
                    current += op.value;
                    break;
                case 'multiply':
                    current *= op.value;
                    break;
                case 'divide':
                    current /= op.value;
                    break;
            }
        });
        return old < current;
    }

    private setVinesPosition(): void {
        this.swingData.forEach((sw: Swing[], i) => {
            const curvePoints = this.curvesPoints[i];
            const segmentPoints = this.newCurvePoints.filter((p) => {
                return p.x >= curvePoints[0].x && p.x <= curvePoints[1].x;
            });
            const vinePosition = this.lowestY(segmentPoints);
            if (!vinePosition) return;
            vinePosition.x = Math.floor(vinePosition.x);
            vinePosition.y = Math.floor(vinePosition.y);
            this.vinePositions.push(vinePosition);
        });
        const index = this.swingData.length; // for the last swing
        const curvePoints = this.curvesPoints[index];
        const segmentPoints = this.newCurvePoints.filter((p) => {
            return p.x >= curvePoints[0].x && p.x <= curvePoints[1].x;
        });
        const vinePosition = this.lowestY(segmentPoints);
        if (!vinePosition) return;
        vinePosition.x = Math.floor(vinePosition.x);
        vinePosition.y = Math.floor(vinePosition.y);
        this.vinePositions.push(vinePosition);
    }

    private setCoinsPosition(): void {
        this.swingData.forEach((sw: Swing[], i) => {
            if (!sw.length) return;
            const curvePoints = this.curvesPoints[i];
            const segmentPoints = this.newCurvePoints.filter((p) => {
                return p.x >= curvePoints[0].x && p.x <= curvePoints[2].x;
            });
            console.info('sw.length', sw.length);
            const positions = this.getRandomPoints(segmentPoints, sw.length, 100);
            if (!positions) return;
            sw.forEach((s, index) => {
                const coin = this.createCoin(s.op);
                coin.position.x = positions[index].x;
                coin.position.y = positions[index].y;
                this.mainCoins.push(coin);
            });
        });
        this.setDummyCoins();
    }

    private setDummyCoins(): void {
        const width = this.newCurvePoints[this.newCurvePoints.length - 1].x;
        const positions = this.getRandomDummyCoinsPoints(
            this.newCurvePoints,
            width * 2,
            this.dummyCoinsCount,
            this.minY,
            this.maxY,
            150,
        );
        positions.forEach((p) => {
            const op = this.getRandomOperation();
            const coin = this.createCoin(op);
            coin.position.x = p.x;
            coin.position.y = p.y;
            this.coins.push(coin);
        });
    }

    private getRandomDummyCoinsPoints(
        given: Vector2[],
        width: number,
        count: number,
        minY: number,
        maxY: number,
        spacing: number,
    ): Vector2[] {
        const s2 = spacing * spacing;
        const out: Vector2[] = [];
        let guard = 0;

        while (out.length < count && guard++ < 10000) {
            const x = Math.random() * width;
            const y = minY + Math.random() * (maxY - minY);

            const far = (a: Vector2) => {
                const dx = x - a.x,
                    dy = y - a.y;
                return dx * dx + dy * dy >= s2;
            };

            if ([...given, ...out].every(far)) out.push(new Vector2(x, y));
        }

        if (out.length < count) throw new Error('Not enough space to place points.');
        return out;
    }

    private createCoin(operation: Operation): Coin {
        const coin = new Coin();
        coin.operation = operation;
        return coin;
    }

    private isLastSwing(): void {
        if (!this.vinePositions.length && !this.allCoinsCollected) {
            const lastY = this.curvesPoints[this.curvesPoints.length - 2][2].y;
            const maxY = this.maxY;
            const percent = Math.abs(maxY - lastY) / this.gameHeight;
            this.eventEmitter.emit('lastSwing', lastY, maxY, percent);
            this.allCoinsCollected = true;
            return;
        }
    }

    private collisionCheck(): void {
        // if (!this.coinsContainer) return;
        let triggerDistance;
        const coinPassed = this.mainCoins.find((c) => {
            triggerDistance = c.operation.function === 'divide' ? 222 : 111;
            // @ts-ignore
            // return c.position.x - triggerDistance < Math.abs(this.coinsContainer.x);
            return c.position.x < Math.abs(this.coinsContainer.x);
        });
        if (!coinPassed) return;
        this.eventEmitter.emit('hit', coinPassed, this.mainCoins.indexOf(coinPassed));
        this.mainCoins.splice(this.mainCoins.indexOf(coinPassed), 1);
        this.collectedCoins.push(coinPassed);
    }

    private checkToSwing(): void {
        if (!this.coinsContainer) return;
        ///@ts-ignore
        const passedVine = this.vinePositions.find((vine) => vine.x - 279 < Math.abs(this.coinsContainer.x));
        if (!passedVine) return;
        this.eventEmitter.emit('swing', this.vinePositions.indexOf(passedVine));
        this.vinePositions.splice(this.vinePositions.indexOf(passedVine), 1);
    }

    private getRandomPoints(segmentPoints: Vector2[], count: number, minSpacing: number): Vector2[] {
        if (count > segmentPoints.length) {
            throw new Error('Count exceeds points length. Cannot fulfill the request.');
        }

        const farFromPoints = (bucket, vector, minSpacing) => {
            return bucket.every((b) => Phaser.Math.Distance.Between(b.x, b.y, vector.x, vector.y) > minSpacing);
        };

        const getRandomPoint = (chosenArray, segmentPoints, count) => {
            const index = Phaser.Math.Between(
              0, segmentPoints.length - 1
              );
            let current = segmentPoints[index];
            if (!farFromPoints(chosenArray, current, minSpacing)) {
                return getRandomPoint(chosenArray, segmentPoints, count);
            }
            console.info('index', current);
            return current;
        };

        console.info('segmentPoints', segmentPoints);
        console.info('count', count);

        const chosen: Vector2[] = [];
        for (let i = 0; i < count; i++) {
            chosen.push(getRandomPoint(chosen, segmentPoints, count));
        }

        return chosen.sort((a, b) => a.x - b.x);
    }

    private lowestY(points: Vector2[]): Vector2 | undefined {
        if (points.length === 0) return undefined;
        let max = points[0];
        for (let i = 1; i < points.length; i++) {
            if (points[i].y > max.y) max = points[i];
        }
        return max;
    }

    private getRandomOperation(): Operation {
        const op = ['add', 'multiply', 'divide'];
        const opIndex = Math.floor(Math.random() * op.length);
        const chosenOp = op[opIndex];
        let value = 2;
        switch (chosenOp) {
            case 'add': {
                const values = [1, 2, 5, 10];
                value = values[Math.floor(Math.random() * values.length)];
                break;
            }
            case 'divide': {
                value = 2;
                break;
            }
            case 'multiply': {
                const values = [5, 4, 3, 2];
                value = values[Math.floor(Math.random() * values.length)];
                break;
            }
        }
        return { function: chosenOp as OpKind, value };
    }
}
